import { useEffect } from "react";

// Maps section [id] -> theme color (background-ish hex matching the section visually).
// Sections not listed fall back to white (or dark for <footer>).
const THEME_COLORS: Record<string, string> = {
  top: "#06081a", // Hero — var(--bg-darker)
  "merchant-portal": "#ebe9ff", // MerchantPortal — light purple gradient endpoint
  solutions: "#0a0d24", // RoutingCascading — var(--bg-dark)
  dashboard: "#f6f9fc", // Dashboard — var(--bg-soft)
  contact: "#f6f9fc", // Contact — var(--bg-soft)
};

const DEFAULT_LIGHT = "#ffffff";
const FOOTER_COLOR = "#06081a";

const resolveColor = (el: Element): string => {
  if (el.id && THEME_COLORS[el.id]) return THEME_COLORS[el.id];
  if (el.tagName === "FOOTER") return FOOTER_COLOR;
  return DEFAULT_LIGHT;
};

export function useThemeColor() {
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;

    const observed = new WeakSet<Element>();

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting element — that's "what the user is reading".
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        meta.setAttribute("content", resolveColor(visible[0].target));
      },
      {
        // Only consider intersections in the band just below the fixed header
        // (top 80px excluded) and well above the bottom half of viewport.
        // This makes exactly one section "current" at a time.
        rootMargin: "-80px 0px -50% 0px",
        threshold: 0,
      }
    );

    const scan = () => {
      document.querySelectorAll("section, footer").forEach((el) => {
        if (!observed.has(el)) {
          observed.add(el);
          intersectionObserver.observe(el);
        }
      });
    };

    scan(); // initial pass

    // Lazy-loaded sections appear after mount — re-scan on DOM additions.
    const mutationObserver = new MutationObserver(scan);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}
